import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationAPI } from '../services/api';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './BulkImport.css';

function BulkImport() {
  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState(null);
  const [previewTeams, setPreviewTeams] = useState(null);
  const [teamErrors, setTeamErrors] = useState({});
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [manualTeam, setManualTeam] = useState({
    name: '',
    sport: '',
    ageGroup: '',
    season: '',
    primaryColor: '#003366',
    secondaryColor: '#FFFFFF',
  });
  const [useOrgDefaults, setUseOrgDefaults] = useState(true);

  const downloadTemplate = () => {
    const csvContent = `Team Name,Sport,Age Group,Season,Primary Color,Secondary Color
Eagles,Baseball,10U,Spring 2025,Red,White
Tigers,Softball,12U,Spring 2025,Navy Blue,Gold
Lions,Baseball,High School,Spring 2025,#228B22,#FFD700
Cardinals,Softball,14U,Spring 2025,,
Wildcats,Baseball,16U,Spring 2025,Purple,`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'team_import_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchActiveSeasons();
    fetchActiveAgeGroups();
  }, []);

  const fetchActiveSeasons = async () => {
    try {
      const response = await adminAPI.getActiveSeasons();
      setSeasons(response.data);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchActiveAgeGroups = async () => {
    try {
      const response = await adminAPI.getActiveAgeGroups();
      setAgeGroups(response.data);
    } catch (error) {
      console.error('Error fetching age groups:', error);
    }
  };

  // Validate a single team and return error message if invalid
  const validateTeam = (team) => {
    const errors = [];

    if (!team.name || !team.name.trim()) {
      errors.push('Team name required');
    }

    if (!team.sport || !team.sport.trim()) {
      errors.push('Sport required');
    } else if (team.sport !== 'Baseball' && team.sport !== 'Softball') {
      errors.push('Invalid sport');
    }

    if (!team.ageGroup || !team.ageGroup.trim()) {
      errors.push('Age group required');
    }

    if (!team.season || !team.season.trim()) {
      errors.push('Season required');
    } else if (!seasons.find((s) => s.name === team.season)) {
      errors.push('Invalid season');
    }

    return errors.length > 0 ? errors.join(', ') : null;
  };

  // Validate all teams when preview loads or teams change
  const validateAllTeams = (teams) => {
    const errors = {};
    teams.forEach((team, index) => {
      const error = validateTeam(team);
      if (error) {
        errors[index] = error;
      }
    });
    setTeamErrors(errors);
  };

  const processFile = (file) => {
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }
    setCsvFile(file);
    setError('');
    parseCSV(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter((line) => line.trim());

      // Skip header row
      const dataLines = lines.slice(1);

      // Helper to normalize age group on frontend
      const normalizeAgeGroupFrontend = (ageGroup) => {
        if (!ageGroup) return '';

        const normalized = ageGroup.trim();

        // Handle number-only formats (6, 8, 10, etc.) → add U
        if (/^\d+$/.test(normalized)) {
          return `${normalized}U`;
        }

        // Handle lowercase u (10u → 10U)
        if (/^\d+u$/i.test(normalized)) {
          return normalized.toUpperCase();
        }

        // Handle space variations (10 u, 10 U → 10U)
        if (/^\d+\s*[uU]$/.test(normalized)) {
          return normalized.replace(/\s/g, '').toUpperCase();
        }

        // Handle common text variations (case-insensitive)
        const textMappings = {
          tball: 'T-Ball',
          't-ball': 'T-Ball',
          'tee ball': 'T-Ball',
          'coach pitch': 'Coach Pitch',
          'kid pitch': 'Kid Pitch',
          minors: 'Minors',
          majors: 'Majors',
          juniors: 'Juniors',
          seniors: 'Seniors',
          'high school': 'High School',
          highschool: 'High School',
          college: 'College',
          adult: 'Adult Rec',
          'adult rec': 'Adult Rec',
        };

        const lower = normalized.toLowerCase();
        if (textMappings[lower]) {
          return textMappings[lower];
        }

        // Return as-is if no normalization matched
        return normalized;
      };

      const teams = dataLines
        .map((line, index) => {
          const values = line.split(',').map((v) => v.trim());
          return {
            name: values[0] || '',
            sport: values[1] || '',
            ageGroup: normalizeAgeGroupFrontend(values[2]) || '',
            season: values[3] || '',
            primaryColor: values[4] || '',
            secondaryColor: values[5] || '',
          };
        })
        .filter((team) => team.name); // Remove empty rows

      if (teams.length === 0) {
        setError('No valid teams found in CSV');
        setPreviewTeams(null);
        return;
      }

      if (teams.length > 100) {
        setError('Maximum 100 teams can be imported at once');
        setPreviewTeams(null);
        return;
      }

      setPreviewTeams(teams);
      setError('');

      // Validate teams after parsing
      setTimeout(() => validateAllTeams(teams), 100); // Small delay to ensure seasons are loaded
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!previewTeams || previewTeams.length === 0) {
      setError('No teams to import');
      return;
    }

    setImporting(true);
    setError('');
    setResult(null);

    try {
      const response = await organizationAPI.bulkCreateTeams(previewTeams);
      setResult(response.data);
      setCsvFile(null);
      setPreviewTeams(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setMessage({
          type: 'error',
          text: 'Organization subscription required for bulk import. Redirecting...',
        });
        setTimeout(() => navigate('/membership'), 2000);
      } else {
        setError(err.response?.data?.error || 'Failed to import teams');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setImporting(true);
    setError('');

    try {
      // If using org defaults, send null for colors
      const teamToCreate = {
        ...manualTeam,
        primaryColor: useOrgDefaults ? null : manualTeam.primaryColor,
        secondaryColor: useOrgDefaults ? null : manualTeam.secondaryColor,
      };

      const response = await organizationAPI.bulkCreateTeams([teamToCreate]);

      if (response.data.created === 1) {
        setMessage({ type: 'success', text: 'Team created successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        setManualTeam({
          name: '',
          sport: '',
          ageGroup: '',
          season: '',
          primaryColor: '#003366',
          secondaryColor: '#FFFFFF',
        });
        setUseOrgDefaults(true);
        // Optionally navigate to home
        navigate('/');
      } else if (response.data.errors && response.data.errors.length > 0) {
        setError(response.data.errors[0].error);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setMessage({
          type: 'error',
          text: 'Organization subscription required. Redirecting...',
        });
        setTimeout(() => navigate('/membership'), 2000);
      } else {
        setError(err.response?.data?.error || 'Failed to create team');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setCsvFile(null);
    setPreviewTeams(null);
    setResult(null);
    setError('');
  };

  return (
    <>
      <Navbar />
      <div className="bulk-import-container">
        <div className="bulk-import-content">
          <div className="form-header">
            <button onClick={() => navigate('/')} className="back-button">
              ← Back to Home
            </button>
            <h1>Bulk Import Teams</h1>
            <p>Upload a CSV file to create multiple teams at once</p>
          </div>

          {!result && (
            <>
              <div className="page-intro">
                <p className="page-description">
                  Create teams individually using the form below,{' '}
                  <strong>OR</strong> upload a CSV file to create multiple teams
                  at once.
                </p>
              </div>

              {/* Manual Team Creation Form */}
              <div className="manual-entry-section">
                <h3>✏️ Create Single Team Manually</h3>
                <form
                  className="manual-team-form"
                  onSubmit={handleManualSubmit}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="manualTeamName">Team Name *</label>
                      <input
                        id="manualTeamName"
                        type="text"
                        value={manualTeam.name}
                        onChange={(e) =>
                          setManualTeam({ ...manualTeam, name: e.target.value })
                        }
                        placeholder=""
                        required
                        disabled={importing}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sport">Sport *</label>
                      <select
                        id="sport"
                        name="sport"
                        value={manualTeam.sport}
                        onChange={(e) =>
                          setManualTeam({
                            ...manualTeam,
                            sport: e.target.value,
                          })
                        }
                        required
                        disabled={importing}
                      >
                        <option value="">Select sport</option>
                        <option value="Baseball">Baseball</option>
                        <option value="Softball">Softball</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="ageGroup">Age Group *</label>
                      <select
                        id="ageGroup"
                        name="ageGroup"
                        value={manualTeam.ageGroup}
                        onChange={(e) =>
                          setManualTeam({
                            ...manualTeam,
                            ageGroup: e.target.value,
                          })
                        }
                        required
                        disabled={importing}
                      >
                        <option value="">Select age group</option>
                        {ageGroups.map((ageGroup) => (
                          <option key={ageGroup.id} value={ageGroup.name}>
                            {ageGroup.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="season">Season *</label>
                      <select
                        id="season"
                        name="season"
                        value={manualTeam.season}
                        onChange={(e) =>
                          setManualTeam({
                            ...manualTeam,
                            season: e.target.value,
                          })
                        }
                        required
                        disabled={importing}
                      >
                        <option value="">Select Season</option>
                        {seasons.map((season) => (
                          <option key={season.id} value={season.name}>
                            {season.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={useOrgDefaults}
                        onChange={(e) => setUseOrgDefaults(e.target.checked)}
                        disabled={importing}
                      />
                      Use Organization Default Colors
                    </label>
                  </div>

                  {!useOrgDefaults && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="primaryColor">Primary Color</label>
                        <div className="color-input-wrapper">
                          <input
                            id="primaryColor"
                            name="primaryColor"
                            type="color"
                            value={manualTeam.primaryColor}
                            onChange={(e) =>
                              setManualTeam({
                                ...manualTeam,
                                primaryColor: e.target.value,
                              })
                            }
                            disabled={importing}
                          />
                          <input
                            type="text"
                            value={manualTeam.primaryColor}
                            onChange={(e) =>
                              setManualTeam({
                                ...manualTeam,
                                primaryColor: e.target.value,
                              })
                            }
                            disabled={importing}
                            placeholder="#003366"
                            className="color-text-input"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="secondaryColor">Secondary Color</label>
                        <div className="color-input-wrapper">
                          <input
                            id="secondaryColor"
                            name="secondaryColor"
                            type="color"
                            value={manualTeam.secondaryColor}
                            onChange={(e) =>
                              setManualTeam({
                                ...manualTeam,
                                secondaryColor: e.target.value,
                              })
                            }
                            disabled={importing}
                          />
                          <input
                            type="text"
                            value={manualTeam.secondaryColor}
                            onChange={(e) =>
                              setManualTeam({
                                ...manualTeam,
                                secondaryColor: e.target.value,
                              })
                            }
                            disabled={importing}
                            placeholder="#FFFFFF"
                            className="color-text-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={importing}
                  >
                    {importing ? 'Creating...' : '+ Create Team'}
                  </button>
                </form>
              </div>

              <div className="divider-text">
                <span>OR</span>
              </div>

              <div className="instructions-box">
                <h3>📋 Bulk Import via CSV:</h3>
                <ol>
                  <li>Download the CSV template below</li>
                  <li>
                    Fill in your team information (Name, Sport, Age Group,
                    Season are required)
                  </li>
                  <li>
                    For colors, use color names (Red, Blue, Navy) OR hex codes
                    (#FF0000)
                  </li>
                  <li>
                    Leave color columns empty to use your organization defaults
                  </li>
                  <li>Upload the completed CSV file</li>
                  <li>Review the preview and click Import</li>
                </ol>
              </div>

              <div className="template-section">
                <p className="template-note">
                  Have you configured your Settings yet?{' '}
                  <button
                    className="link-button"
                    onClick={() => navigate('/organization/settings')}
                  >
                    Configure first!
                  </button>
                </p>

                <p>
                  <button
                    className="btn-download-template"
                    onClick={downloadTemplate}
                  >
                    📥 Download CSV Template
                  </button>
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="upload-section">
                <label
                  htmlFor="csv-upload"
                  className={`upload-box ${dragActive ? 'drag-active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {csvFile ? (
                    <>
                      <span className="upload-icon">✅</span>
                      <p className="upload-filename">{csvFile.name}</p>
                      <p className="upload-hint">
                        Click to change file or drag and drop
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="upload-icon">📄</span>
                      <p>Click to upload CSV file</p>
                      <p className="upload-hint">or drag and drop</p>
                    </>
                  )}
                </label>
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="csv-file-input"
                />
              </div>

              {previewTeams && previewTeams.length > 0 && (
                <div className="preview-section">
                  <h3>👀 Preview ({previewTeams.length} teams)</h3>
                  <div className="preview-table-wrapper">
                    <table className="preview-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Team Name</th>
                          <th>Sport</th>
                          <th>Age Group</th>
                          <th>Season</th>
                          <th>Colors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewTeams.map((team, index) => (
                          <tr
                            key={index}
                            className={teamErrors[index] ? 'row-error' : ''}
                          >
                            <td>{index + 1}</td>
                            <td>
                              <input
                                type="text"
                                value={team.name}
                                onChange={(e) => {
                                  const updated = [...previewTeams];
                                  updated[index].name = e.target.value;
                                  setPreviewTeams(updated);
                                  validateAllTeams(updated);
                                }}
                                className="inline-edit-input"
                                placeholder="Team name"
                              />
                            </td>
                            <td>
                              <select
                                value={team.sport}
                                onChange={(e) => {
                                  const updated = [...previewTeams];
                                  updated[index].sport = e.target.value;
                                  setPreviewTeams(updated);
                                  validateAllTeams(updated);
                                }}
                                className="inline-edit-select"
                              >
                                <option value="">Select...</option>
                                <option value="Baseball">Baseball</option>
                                <option value="Softball">Softball</option>
                              </select>
                            </td>
                            <td>
                              <select
                                value={team.ageGroup}
                                onChange={(e) => {
                                  const updated = [...previewTeams];
                                  updated[index].ageGroup = e.target.value;
                                  setPreviewTeams(updated);
                                  validateAllTeams(updated);
                                }}
                                className="inline-edit-select"
                              >
                                <option value="">Select...</option>
                                {ageGroups.map((ageGroup) => (
                                  <option
                                    key={ageGroup.id}
                                    value={ageGroup.name}
                                  >
                                    {ageGroup.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                value={team.season}
                                onChange={(e) => {
                                  const updated = [...previewTeams];
                                  updated[index].season = e.target.value;
                                  setPreviewTeams(updated);
                                  validateAllTeams(updated);
                                }}
                                className="inline-edit-select"
                              >
                                <option value="">Select...</option>
                                {seasons.map((season) => (
                                  <option key={season.id} value={season.name}>
                                    {season.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {teamErrors[index] ? (
                                <span
                                  className="error-indicator"
                                  title={teamErrors[index]}
                                >
                                  ⚠️ {teamErrors[index]}
                                </span>
                              ) : team.primaryColor || team.secondaryColor ? (
                                <div className="color-indicators">
                                  {team.primaryColor && (
                                    <span
                                      className="color-dot"
                                      style={{
                                        backgroundColor: team.primaryColor,
                                      }}
                                      title={team.primaryColor}
                                    />
                                  )}
                                  {team.secondaryColor && (
                                    <span
                                      className="color-dot"
                                      style={{
                                        backgroundColor: team.secondaryColor,
                                      }}
                                      title={team.secondaryColor}
                                    />
                                  )}
                                </div>
                              ) : (
                                <span className="default-indicator">
                                  Using defaults
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="preview-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleImport}
                      disabled={importing || Object.keys(teamErrors).length > 0}
                    >
                      {importing
                        ? 'Importing...'
                        : Object.keys(teamErrors).length > 0
                          ? `Fix ${Object.keys(teamErrors).length} Error${
                              Object.keys(teamErrors).length !== 1 ? 's' : ''
                            } First`
                          : `Import ${previewTeams.length} Teams`}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleReset}
                      disabled={importing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="result-section">
              <div className="result-header">
                <h2>✅ Import Complete!</h2>
                <p>
                  Successfully created {result.created} out of {result.total}{' '}
                  teams
                </p>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="errors-box">
                  <h3>⚠️ Errors ({result.errors.length})</h3>
                  <ul>
                    {result.errors.map((err, index) => (
                      <li key={index}>
                        <strong>
                          Row {err.row} ({err.name}):
                        </strong>{' '}
                        {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="result-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/')}
                >
                  View My Teams
                </button>
                <button className="btn btn-primary" onClick={handleReset}>
                  Import More Teams
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Toast Notifications */}
        {message.text && message.type === 'success' && (
          <div className="toast-message toast-success">{message.text}</div>
        )}
        {message.text && message.type === 'error' && (
          <div className="toast-message toast-error">{message.text}</div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default BulkImport;
